import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import useAppCart from "@/hooks/useAppCart";
import useAppInstall from "@/hooks/useAppInstall";
import useAppNotifications from "@/hooks/useAppNotifications";
import useCustomerAppSession from "@/hooks/useCustomerAppSession";
import {
  usePublicCategories,
  usePublicProducts,
  usePublicSettings,
} from "@/hooks/usePublicSiteData";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Bell,
  Clock3,
  CheckCircle2,
  Download,
  Minus,
  Package2,
  Plus,
  RefreshCcw,
  ScanSearch,
  Search,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Store,
  Truck,
  Wifi,
  WifiOff,
  X,
  Sparkles,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EMPTY_CHECKOUT = {
  customer_name: "",
  company_name: "",
  email: "",
  phone: "",
  country: "",
  city: "",
  address: "",
  preferred_contact: "email",
  notes: "",
};

const TABS = [
  { key: "shop", label: "Shop", icon: Store },
  { key: "notifications", label: "Alerts", icon: Bell },
  { key: "track", label: "Track", icon: ScanSearch },
];

const APP_STATE_KEY = "rsi_app_state_v2";
const CHECKOUT_DRAFT_KEY = "rsi_app_checkout_draft_v1";
const PORTAL_PREFILL_KEY = "rsi_home_portal_prefill_v1";

function parseListInput(value) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function readLocalJson(key, fallback) {
  if (typeof localStorage === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export default function AppMarketplace() {
  const savedUiState = readLocalJson(APP_STATE_KEY, {});
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(savedUiState.activeTab || "shop");
  const [query, setQuery] = useState(savedUiState.query || "");
  const [category, setCategory] = useState(savedUiState.category || "all");
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(null);
  const [issuedCredentials, setIssuedCredentials] = useState(null);
  const [trackId, setTrackId] = useState(savedUiState.trackId || "");
  const [customerAccessId, setCustomerAccessId] = useState("");
  const [customerAccessCode, setCustomerAccessCode] = useState("");
  const [customerAuthError, setCustomerAuthError] = useState("");
  const [qtyMap, setQtyMap] = useState({});
  const [checkout, setCheckout] = useState(() => ({
    ...EMPTY_CHECKOUT,
    ...readLocalJson(CHECKOUT_DRAFT_KEY, {}),
  }));
  const cart = useAppCart();
  const { canInstall, install, isStandalone, isOnline, isIOS } = useAppInstall();
  const customerSession = useCustomerAppSession();
  const activeOrder = orderPlaced?.order || null;

  const { data: settings } = usePublicSettings();
  const { data: categories = [] } = usePublicCategories();
  const { data: products = [], isLoading } = usePublicProducts(["public-products-app"], "/products");

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return products.filter((product) => {
      const matchesCategory = category === "all" || product.category_slug === category;
      const haystack = [
        product.name,
        product.short_description,
        product.price_range,
        product.origin_country,
        product.hs_code,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return matchesCategory && (!normalizedQuery || haystack.includes(normalizedQuery));
    });
  }, [products, query, category]);

  const notifications = useAppNotifications({
    orderNumber: activeOrder?.order_number || "",
  });

  useEffect(() => {
    if (customerSession.lastAccessId && !customerAccessId) {
      setCustomerAccessId(customerSession.lastAccessId);
    }
  }, [customerAccessId, customerSession.lastAccessId]);

  useEffect(() => {
    if (typeof sessionStorage === "undefined") return;
    try {
      const raw = sessionStorage.getItem(PORTAL_PREFILL_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.customerAccessId && !customerAccessId) {
        setCustomerAccessId(String(parsed.customerAccessId).toUpperCase());
      }
      if (parsed?.customerAccessCode && !customerAccessCode) {
        setCustomerAccessCode(String(parsed.customerAccessCode));
      }
      if (parsed?.customerAccessId || parsed?.customerAccessCode) {
        setActiveTab("track");
      }
      sessionStorage.removeItem(PORTAL_PREFILL_KEY);
    } catch {
      // Ignore malformed prefill payloads.
    }
  }, [customerAccessCode, customerAccessId]);

  useEffect(() => {
    if (location.hash !== "#customer-workspace") return;
    setActiveTab("track");
    const timer = window.setTimeout(() => {
      document.getElementById("customer-workspace")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 140);
    return () => window.clearTimeout(timer);
  }, [location.hash, customerSession.isAuthenticated]);

  useEffect(() => {
    const customer = customerSession.session?.customer;
    if (!customer) return;
    notifications.setProfile((current) => ({
      ...current,
      customer_name: current.customer_name || customer.customer_name || "",
      email: current.email || customer.email || "",
      phone: current.phone || customer.phone || "",
      order_numbers: Array.from(new Set([...(current.order_numbers || []), ...(customer.order_numbers || [])])),
      tracking_ids: Array.from(new Set([...(current.tracking_ids || []), ...(customer.tracking_ids || [])])),
    }));
  }, [customerSession.session, notifications]);

  useEffect(() => {
    localStorage.setItem(
      APP_STATE_KEY,
      JSON.stringify({
        activeTab,
        query,
        category,
        trackId,
      })
    );
  }, [activeTab, category, query, trackId]);

  useEffect(() => {
    localStorage.setItem(CHECKOUT_DRAFT_KEY, JSON.stringify(checkout));
  }, [checkout]);

  useEffect(() => {
    setCheckout((current) => ({
      ...current,
      customer_name: current.customer_name || notifications.profile.customer_name || "",
      email: current.email || notifications.profile.email || "",
      phone: current.phone || notifications.profile.phone || "",
    }));
  }, [
    notifications.profile.customer_name,
    notifications.profile.email,
    notifications.profile.phone,
  ]);

  const categoryNameMap = useMemo(
    () =>
      Object.fromEntries(categories.map((item) => [item.slug, item.name])),
    [categories]
  );

  const smartCategorySlugs = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...(notifications.profile.interested_categories || []),
            ...cart.items.map((item) => item.category_slug),
            category !== "all" ? category : "",
          ].filter(Boolean)
        )
      ),
    [cart.items, category, notifications.profile.interested_categories]
  );

  const recommendedProducts = useMemo(() => {
    const cartProductIds = new Set(cart.items.map((item) => item.product_id));
    const cartCategoryCounts = cart.items.reduce((acc, item) => {
      acc[item.category_slug] = (acc[item.category_slug] || 0) + item.quantity;
      return acc;
    }, {});
    const normalizedQuery = query.trim().toLowerCase();
    const ranked = filteredProducts
      .map((product) => {
        let score = product.featured ? 10 : 0;
        if (smartCategorySlugs.includes(product.category_slug)) score += 18;
        if (cartCategoryCounts[product.category_slug]) score += 12 + cartCategoryCounts[product.category_slug];
        if (normalizedQuery && product.name.toLowerCase().includes(normalizedQuery)) score += 8;
        if (notifications.notifications.some((item) => item.product_id === product.id)) score += 6;
        if (cartProductIds.has(product.id)) score -= 20;
        return { product, score };
      })
      .sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score;
        return String(right.product.created_at || "").localeCompare(String(left.product.created_at || ""));
      });
    return ranked.map((item) => item.product);
  }, [
    cart.items,
    filteredProducts,
    notifications.notifications,
    query,
    smartCategorySlugs,
  ]);

  const featuredProducts = useMemo(
    () => recommendedProducts.filter((product) => product.featured).slice(0, 3),
    [recommendedProducts]
  );

  const liveCatalogSnapshot = useMemo(() => {
    const productCategories = new Set(products.map((item) => item.category_slug).filter(Boolean));
    const productOrigins = new Set(products.map((item) => item.origin_country).filter(Boolean));
    const activeCategories = categories.filter((item) => productCategories.has(item.slug));
    const latestProducts = [...products]
      .sort((left, right) =>
        String(right.created_at || "").localeCompare(String(left.created_at || ""))
      )
      .slice(0, 3);

    return {
      productCount: products.length,
      activeCategoryCount: activeCategories.length || productCategories.size,
      featuredCount: products.filter((item) => item.featured).length,
      originCount: productOrigins.size,
      activeCategories,
      latestProducts,
    };
  }, [categories, products]);

  const displayedProducts = useMemo(() => recommendedProducts, [recommendedProducts]);
  const savedTrackingIds = useMemo(
    () => notifications.profile.tracking_ids || [],
    [notifications.profile.tracking_ids]
  );
  const savedOrderNumbers = useMemo(
    () => notifications.profile.order_numbers || [],
    [notifications.profile.order_numbers]
  );
  const customerOrders = customerSession.session?.orders || [];
  const customerShipments = customerSession.session?.shipments || [];

  const alertSummary = useMemo(
    () =>
      notifications.notifications.reduce(
        (acc, item) => {
          acc.total += 1;
          acc[item.type] = (acc[item.type] || 0) + 1;
          return acc;
        },
        { total: 0, product: 0, shipment: 0, delivery: 0, order: 0 }
      ),
    [notifications.notifications]
  );

  const smartActions = useMemo(() => {
    const actions = [];
    if (notifications.permission !== "granted") {
      actions.push({
        label: "Enable smart alerts",
        note: "Turn on product and shipment updates.",
        onClick: () => {
          setActiveTab("notifications");
          notifications.requestPermission();
        },
      });
    }
    if (cart.items.length) {
      actions.push({
        label: "Resume checkout",
        note: `${cart.totalUnits} units are waiting in the cart.`,
        onClick: () => setCartOpen(true),
      });
    }
    if (savedTrackingIds[0]) {
      actions.push({
        label: "Track latest shipment",
        note: `Open ${savedTrackingIds[0]} instantly.`,
        onClick: () => {
          setActiveTab("track");
          setTrackId(savedTrackingIds[0]);
        },
      });
    }
    if (smartCategorySlugs[0] && category !== smartCategorySlugs[0]) {
      actions.push({
        label: "Browse best-match products",
        note: `${categoryNameMap[smartCategorySlugs[0]] || smartCategorySlugs[0]} products are ranked first for you.`,
        onClick: () => {
          setActiveTab("shop");
          setCategory(smartCategorySlugs[0]);
        },
      });
    }
    if (savedOrderNumbers[0]) {
      actions.push({
        label: "Review order updates",
        note: `Watch ${savedOrderNumbers[0]} in alerts.`,
        onClick: () => setActiveTab("notifications"),
      });
    }
    return actions.slice(0, 4);
  }, [
    cart.items.length,
    cart.totalUnits,
    category,
    categoryNameMap,
    notifications,
    savedOrderNumbers,
    savedTrackingIds,
    smartCategorySlugs,
  ]);

  const smartSearchChips = useMemo(() => {
    const chips = [];
    if (smartCategorySlugs[0]) {
      chips.push({
        label: categoryNameMap[smartCategorySlugs[0]] || smartCategorySlugs[0],
        onClick: () => setCategory(smartCategorySlugs[0]),
      });
    }
    const country = displayedProducts.find((item) => item.origin_country)?.origin_country;
    if (country) {
      chips.push({
        label: country,
        onClick: () => setQuery(country),
      });
    }
    const hsCode = displayedProducts.find((item) => item.hs_code)?.hs_code;
    if (hsCode) {
      chips.push({
        label: hsCode,
        onClick: () => setQuery(hsCode),
      });
    }
    if (featuredProducts.length) {
      chips.push({
        label: "Featured",
        onClick: () => setQuery(""),
      });
    }
    return chips.slice(0, 4);
  }, [categoryNameMap, displayedProducts, featuredProducts.length, smartCategorySlugs]);

  const placeOrderMut = useMutation({
    mutationFn: async (payload) => (await api.post("/orders", payload)).data,
    onSuccess: async (result) => {
      const order = result.order;
      const customerAccess = result.customer_access;
      cart.clearCart();
      setOrderPlaced(result);
      setIssuedCredentials(customerAccess?.just_issued_credentials ? customerAccess : null);
      setCheckoutOpen(false);
      setCartOpen(false);
      setCheckout(EMPTY_CHECKOUT);
      localStorage.removeItem(CHECKOUT_DRAFT_KEY);
      setActiveTab("notifications");
      if (customerAccess?.customer_access_id) {
        setCustomerAccessId(customerAccess.customer_access_id);
      }

      const nextProfile = {
        ...notifications.profile,
        customer_name: checkout.customer_name || notifications.profile.customer_name,
        email: checkout.email || notifications.profile.email,
        phone: checkout.phone || notifications.profile.phone,
        order_numbers: Array.from(
          new Set([...(notifications.profile.order_numbers || []), order.order_number])
        ),
      };
      notifications.setProfile(nextProfile);
      try {
        await notifications.saveSubscription(nextProfile);
      } catch {
        // Save errors are already surfaced in the hook.
      }
      customerSession.refresh().catch(() => {});
      toast.success("Order placed successfully");
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  useEffect(() => {
    if (!activeOrder?.order_number) return;
    const existingOrders = notifications.profile.order_numbers || [];
    if (existingOrders.includes(activeOrder.order_number)) return;
    notifications.setProfile((current) => ({
      ...current,
      order_numbers: [...(current.order_numbers || []), activeOrder.order_number],
    }));
  }, [activeOrder, notifications]);

  const setProductQty = (productId, nextQty) => {
    setQtyMap((current) => ({ ...current, [productId]: Math.max(1, nextQty) }));
  };

  const submitOrder = (event) => {
    event.preventDefault();
    if (!cart.items.length) {
      toast.error("Add products to the cart first");
      return;
    }
    placeOrderMut.mutate({
      ...checkout,
      items: cart.items.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
      })),
    });
  };

  const openTrack = (event) => {
    event.preventDefault();
    const cleaned = trackId.trim().toUpperCase();
    if (!cleaned) return;
    if (!savedTrackingIds.includes(cleaned)) {
      const nextProfile = {
        ...notifications.profile,
        tracking_ids: Array.from(new Set([...(savedTrackingIds || []), cleaned])),
      };
      notifications.setProfile(nextProfile);
      notifications.saveSubscription(nextProfile).catch(() => {});
    }
    navigate(`/track?id=${encodeURIComponent(cleaned)}`);
  };

  const loginCustomerWorkspace = async (event) => {
    event.preventDefault();
    setCustomerAuthError("");
    const result = await customerSession.login(customerAccessId, customerAccessCode);
    if (!result.ok) {
      setCustomerAuthError(result.error);
      return;
    }
    setCustomerAccessCode("");
    setIssuedCredentials(null);
    toast.success("Customer workspace opened");
    navigate("/client-portal");
  };

  const unreadCount = notifications.notifications.length;

  return (
    <>
      <section className="app-page-shell bg-[var(--rsi-bg-subtle)] py-10 md:py-14">
        <div className="container-rsi">
          <div className="app-mobile-frame rounded-[32px] border border-white/60 bg-white/75 p-4 md:p-6 shadow-[0_30px_80px_-20px_rgba(42,19,65,0.28)] backdrop-blur-xl">
            <div className="app-overview-grid grid gap-6 items-start">
              <div className="app-hero-column">
                <div className="app-hero-intro surface-card p-6 lg:p-7">
                  <div className="app-flow-header">
                    <span className="app-flow-step">01</span>
                    <span className="app-flow-label">App overview</span>
                  </div>
                  <div className="app-hero-intro__grid">
                    <div className="max-w-3xl">
                      <span className="badge-purple">
                        <Smartphone className="h-3.5 w-3.5" /> Android + iPhone optimized
                      </span>
                      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-4 text-[var(--rsi-navy)] leading-[1.02]">
                        A stronger customer app for
                        <br />
                        <span className="text-[var(--rsi-purple)]">
                          shopping, alerts, and shipment visibility.
                        </span>
                      </h1>
                      <p className="mt-4 text-gray-600 max-w-2xl text-base sm:text-lg leading-relaxed">
                        Customers can now use one app to discover new products, receive app alerts,
                        place orders, and follow shipment progress with a cleaner mobile experience on
                        both Android and iPhone.
                      </p>

                      <div className="app-hero-actions mt-6">
                        <div className="app-hero-actions__primary">
                          <button
                            type="button"
                            onClick={() => setCartOpen(true)}
                            className="btn-primary app-hero-primary-btn"
                            data-testid="app-cart-open"
                          >
                            <ShoppingCart className="h-4 w-4" /> Cart ({cart.totalUnits})
                          </button>
                          {canInstall ? (
                            <button type="button" onClick={install} className="btn-ghost app-hero-secondary-btn">
                              <Download className="h-4 w-4" /> Install app
                            </button>
                          ) : null}
                        </div>
                        <div className="app-hero-actions__secondary">
                          <button type="button" onClick={() => setActiveTab("track")} className="btn-ghost app-hero-link-btn">
                            <ArrowUpRight className="h-4 w-4" /> Open tracking
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="app-hero-sidefacts">
                      <div className="app-hero-mini-card">
                        <div className="app-hero-mini-card__icon">
                          <RefreshCcw className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="app-hero-mini-card__label">Live sync</div>
                          <div className="app-hero-mini-card__value">Website, app, and admin stay connected.</div>
                        </div>
                      </div>
                      <div className="app-hero-mini-card">
                        <div className="app-hero-mini-card__icon">
                          <Clock3 className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="app-hero-mini-card__label">Faster return journey</div>
                          <div className="app-hero-mini-card__value">Customers reopen alerts, orders, and tracking with less friction.</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-4 app-hero-stats">
                  <div className="surface-card app-stat-card p-5">
                    <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Catalog</div>
                    <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
                      {products.length}
                    </div>
                    <div className="mt-2 text-sm text-gray-600">Live products from admin.</div>
                  </div>
                  <div className="surface-card app-stat-card p-5">
                    <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Alerts</div>
                    <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
                      {unreadCount}
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      Latest app notifications in your inbox.
                    </div>
                  </div>
                  <div className="surface-card app-stat-card p-5">
                    <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Connection</div>
                    <div className="mt-2 flex items-center gap-2 font-display text-xl font-black tracking-tight text-[var(--rsi-navy)]">
                      {isOnline ? (
                        <Wifi className="h-5 w-5 text-[var(--rsi-green)]" />
                      ) : (
                        <WifiOff className="h-5 w-5 text-amber-500" />
                      )}
                      {isOnline ? "Live" : "Offline"}
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      Handles live refresh with mobile-safe fallbacks.
                    </div>
                  </div>
                  <div className="surface-card app-stat-card p-5">
                    <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                      Install mode
                    </div>
                    <div className="mt-2 font-display text-xl font-black tracking-tight text-[var(--rsi-navy)]">
                      {isStandalone ? "App mode" : isIOS ? "iPhone ready" : "Browser ready"}
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      {isIOS
                        ? "Includes iPhone-friendly install and safe-area handling."
                        : "Improved for standalone mobile use."}
                    </div>
                  </div>
                  <div className="surface-card app-stat-card app-stat-card--wide p-5 sm:col-span-2">
                    <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                      Trade desk
                    </div>
                    <div className="mt-2 font-display text-xl font-black tracking-tight text-[var(--rsi-navy)]">
                      {settings?.company_name || "R.S. International"}
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      Product launches, orders, and shipment support in one app.
                    </div>
                  </div>
                </div>
              </div>

              <div className="premium-panel app-preview-grid showcase-hero-panel px-6 py-6 text-white">
                <div className="app-flow-header app-flow-header--light">
                  <span className="app-flow-step app-flow-step--light">02</span>
                  <span className="app-flow-label app-flow-label--light">Product experience</span>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-2">
                    <span className="app-showcase-chip">
                      <Store className="h-3.5 w-3.5" /> {liveCatalogSnapshot.productCount} live products
                    </span>
                    <span className="app-showcase-chip">
                      <ShieldCheck className="h-3.5 w-3.5" /> Admin-managed catalog
                    </span>
                  </div>
                  <div className="showcase-kicker">
                    <RefreshCcw className="h-4 w-4" />
                    Real backend sync
                  </div>
                </div>

                <div className="mt-6 grid gap-5">
                  <div className="showcase-copy-stack">
                    <div className="showcase-highlight-box">
                      <div className="showcase-eyebrow">Live catalog status</div>
                      <h2 className="font-display text-3xl font-black tracking-tight leading-tight text-white sm:text-[2.25rem]">
                        The app now reads the same real catalog used by the website and admin panel.
                      </h2>
                      <p className="mt-3 text-sm sm:text-base text-white/92 leading-relaxed">
                        {liveCatalogSnapshot.productCount > 0
                          ? `${settings?.company_name || "R.S. International"} is currently publishing ${liveCatalogSnapshot.productCount} active product${liveCatalogSnapshot.productCount === 1 ? "" : "s"} across ${liveCatalogSnapshot.activeCategoryCount} categor${liveCatalogSnapshot.activeCategoryCount === 1 ? "y" : "ies"}. Any product changes made from the admin panel appear here without mock fallbacks.`
                          : `The mock catalog has been removed. This app section now waits for real products to be published from the admin panel, so customers only ever see live business data.`}
                      </p>
                    </div>

                    <div className="showcase-value-strip">
                      <div className="showcase-value-pill">Website-linked</div>
                      <div className="showcase-value-pill">Admin-controlled</div>
                      <div className="showcase-value-pill">No mock fallback</div>
                    </div>

                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                      <div className="metric-tile">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-white/72">Live categories</div>
                        <div className="mt-2 text-base font-semibold text-white">
                          {liveCatalogSnapshot.activeCategoryCount} active category
                          {liveCatalogSnapshot.activeCategoryCount === 1 ? "" : "ies"}
                        </div>
                        <div className="mt-1 text-sm text-white/82">
                          Categories shown in the app come from current backend records, not bundled sample data.
                        </div>
                      </div>
                      <div className="metric-tile">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-white/72">Featured products</div>
                        <div className="mt-2 text-base font-semibold text-white">
                          {liveCatalogSnapshot.featuredCount} highlighted item
                          {liveCatalogSnapshot.featuredCount === 1 ? "" : "s"}
                        </div>
                        <div className="mt-1 text-sm text-white/82">
                          Featured placement now reflects the real backend catalog instead of a demo showcase.
                        </div>
                      </div>
                    </div>

                    <div className="showcase-direction-card mt-6 rounded-[28px] border border-white/12 bg-white/8 p-5">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-xs uppercase tracking-[0.18em] text-white/72">Catalog coverage</div>
                          <div className="mt-2 text-lg font-bold text-white">
                            {liveCatalogSnapshot.originCount} sourcing origin
                            {liveCatalogSnapshot.originCount === 1 ? "" : "s"} currently listed
                          </div>
                        </div>
                        <div className="h-12 w-12 rounded-2xl bg-white/12 grid place-items-center">
                          <Truck className="h-5 w-5" />
                        </div>
                      </div>
                      <div className="mt-3 text-sm text-white/84">
                        {liveCatalogSnapshot.originCount > 0
                          ? "Country, MOQ, price label, and category information are now read from the live product records customers actually browse."
                          : "Once live products are added from the admin panel, sourcing origin, MOQ, pricing labels, and category detail will appear here automatically."}
                      </div>
                    </div>
                  </div>

                  <div className="showcase-phone-shell">
                    <div className="showcase-phone-glow" />
                    <div className="showcase-phone-topbar">
                      <span className="showcase-dot" />
                      <span className="showcase-dot" />
                      <span className="showcase-dot" />
                    </div>
                    <div className="showcase-phone-card">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-[11px] uppercase tracking-[0.18em] text-white/72">Recent catalog</div>
                          <div className="mt-2 text-xl font-bold text-white">Live product feed</div>
                        </div>
                        <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/12">
                          <Package2 className="h-5 w-5" />
                        </div>
                      </div>
                      <div className="mt-4 space-y-3">
                        {liveCatalogSnapshot.latestProducts.length ? (
                          liveCatalogSnapshot.latestProducts.map((product) => (
                            <div key={product.id} className="showcase-mini-row">
                              <div className="showcase-mini-icon">
                                <Store className="h-4 w-4" />
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-white">{product.name}</div>
                                <div className="text-xs text-white/82">
                                  {product.origin_country || "Origin pending"} · {product.price_range || "Price on request"}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="rounded-3xl border border-white/12 bg-white/6 px-4 py-4 text-sm text-white/82">
                            No live products have been published yet. When the admin team adds real catalog records, the newest items will appear here automatically.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="app-showcase-grid mt-6 grid gap-6">
              <div className="premium-panel app-preview-grid showcase-security-panel p-6 text-white">
                <div className="app-flow-header app-flow-header--light">
                  <span className="app-flow-step app-flow-step--light">03</span>
                  <span className="app-flow-label app-flow-label--light">Private customer access</span>
                </div>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="max-w-2xl">
                    <span className="badge-purple showcase-security-badge !border-white/15 !bg-white/10 !text-white">
                      <ShieldCheck className="h-3.5 w-3.5" /> Private customer workspace
                    </span>
                    <h2 className="mt-4 font-display text-3xl font-black tracking-tight leading-tight sm:text-[2.05rem]">
                      Each customer can access only their own orders and shipments.
                    </h2>
                    <p className="mt-3 text-sm sm:text-base text-white/90 leading-relaxed">
                      The app supports a customer access ID and access code workflow. Once a
                      customer signs in, the workspace shows only the orders and shipment records
                      linked to that customer profile.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 text-[11px]">
                    <span className="badge-purple showcase-security-badge !border-white/15 !bg-white/10 !text-white">Private IDs</span>
                    <span className="badge-purple showcase-security-badge !border-white/15 !bg-white/10 !text-white">Own orders only</span>
                    <span className="badge-purple showcase-security-badge !border-white/15 !bg-white/10 !text-white">Own shipments only</span>
                  </div>
                </div>

                <div className="mt-6 grid gap-4">
                  <div className="grid sm:grid-cols-3 gap-3 text-sm showcase-security-metrics">
                    <div className="metric-tile">
                      <div className="text-white/74 uppercase tracking-[0.18em] text-[11px]">Orders</div>
                      <div className="mt-2 font-display text-2xl font-black">{customerOrders.length}</div>
                      <div className="mt-1 text-white/86">Visible inside the signed-in customer workspace.</div>
                    </div>
                    <div className="metric-tile">
                      <div className="text-white/74 uppercase tracking-[0.18em] text-[11px]">Shipments</div>
                      <div className="mt-2 font-display text-2xl font-black">{customerShipments.length}</div>
                      <div className="mt-1 text-white/86">Linked shipment records for this customer session.</div>
                    </div>
                    <div className="metric-tile">
                      <div className="text-white/74 uppercase tracking-[0.18em] text-[11px]">Store path</div>
                      <div className="mt-2 font-display text-2xl font-black">Ready</div>
                      <div className="mt-1 text-white/86">PWA-first app shell prepared for Android and iPhone packaging.</div>
                    </div>
                  </div>

                  <div className="showcase-security-card">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-white/74">Access protection</div>
                        <div className="mt-2 text-lg font-bold text-white">Customer-specific identity gate</div>
                      </div>
                      <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/12">
                        <ShieldCheck className="h-5 w-5" />
                      </div>
                    </div>
                    <div className="mt-4 space-y-3">
                      <div className="showcase-mini-row">
                        <div className="showcase-mini-icon">
                          <CheckCircle2 className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-white">Dedicated customer ID</div>
                          <div className="text-xs text-white/82">Each customer signs in with a unique access identity.</div>
                        </div>
                      </div>
                      <div className="showcase-mini-row">
                        <div className="showcase-mini-icon">
                          <Package2 className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-white">Own records only</div>
                          <div className="text-xs text-white/82">Orders and shipments stay scoped to that one customer account.</div>
                        </div>
                      </div>
                      <div className="showcase-mini-row">
                        <div className="showcase-mini-icon">
                          <Smartphone className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-white">Ready across devices</div>
                          <div className="text-xs text-white/82">The same private workspace remains clean on mobile and desktop.</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div id="customer-workspace" className="surface-card workspace-shell-card p-0">
                <div className="workspace-shell-card__header">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Customer sign-in</div>
                      <h3 className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                        Open the private app workspace
                      </h3>
                      <p className="mt-2 max-w-xl text-sm text-gray-600">
                        Use a customer access ID and access code to open a private workspace tied only to that customer.
                      </p>
                    </div>
                    {customerSession.isAuthenticated ? (
                      <button type="button" onClick={customerSession.logout} className="btn-ghost !px-4 !py-2.5 text-xs workspace-shell-card__signout">
                        Sign out
                      </button>
                    ) : null}
                  </div>

                  <div className="workspace-shell-card__trust-strip">
                    <div className="workspace-shell-card__trust-pill">Customer-specific access</div>
                    <div className="workspace-shell-card__trust-pill">Own orders only</div>
                    <div className="workspace-shell-card__trust-pill">Own shipments only</div>
                  </div>
                </div>

                {customerSession.isAuthenticated ? (
                  <div className="workspace-shell-card__body mt-0 space-y-4">
                    <div className="workspace-session-banner rounded-3xl border border-[var(--rsi-purple)]/8 bg-[linear-gradient(180deg,#faf7ff_0%,#f5f0fb_100%)] p-5">
                      <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Signed in as</div>
                      <div className="mt-2 font-semibold text-[var(--rsi-navy)]">
                        {customerSession.session?.customer?.customer_name || "Customer"}
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        Access ID: {customerSession.session?.customer?.customer_access_id}
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="workspace-panel rounded-3xl border border-gray-100 bg-white/80 p-4 shadow-[0_12px_30px_-24px_rgba(42,19,65,0.35)]">
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">My orders</div>
                        <div className="mt-2 space-y-2">
                          {customerOrders.length ? customerOrders.slice(0, 3).map((item) => (
                            <div key={item.id} className="workspace-entry rounded-2xl bg-[var(--rsi-bg-subtle)] px-3 py-3 text-sm shadow-sm">
                              <div className="font-semibold text-[var(--rsi-navy)]">{item.order_number}</div>
                              <div className="mt-1 text-gray-600 capitalize">{String(item.status || "").replaceAll("_", " ")}</div>
                            </div>
                          )) : <div className="text-sm text-gray-500">No customer orders linked yet.</div>}
                        </div>
                      </div>
                      <div className="workspace-panel rounded-3xl border border-gray-100 bg-white/80 p-4 shadow-[0_12px_30px_-24px_rgba(42,19,65,0.35)]">
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">My shipments</div>
                        <div className="mt-2 space-y-2">
                          {customerShipments.length ? customerShipments.slice(0, 3).map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => navigate(`/track?id=${encodeURIComponent(item.tracking_id)}`)}
                              className="workspace-entry w-full rounded-2xl bg-[var(--rsi-bg-subtle)] px-3 py-3 text-left text-sm transition hover:-translate-y-0.5 hover:shadow-sm"
                            >
                              <div className="font-semibold text-[var(--rsi-navy)]">{item.tracking_id}</div>
                              <div className="mt-1 text-gray-600">{item.current_location || item.destination || "Shipment open"}</div>
                            </button>
                          )) : <div className="text-sm text-gray-500">No customer shipments linked yet.</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={loginCustomerWorkspace} className="workspace-shell-card__body mt-0 grid gap-4">
                    <div>
                      <Label htmlFor="customer-access-id">Customer Access ID</Label>
                      <Input
                        id="customer-access-id"
                        value={customerAccessId}
                        onChange={(event) => setCustomerAccessId(event.target.value.toUpperCase())}
                        placeholder="RSIC-260620-ABC123"
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="customer-access-code">Access Code</Label>
                      <Input
                        id="customer-access-code"
                        type="password"
                        value={customerAccessCode}
                        onChange={(event) => setCustomerAccessCode(event.target.value.toUpperCase())}
                        placeholder="ABCD-EFGH-JKLM"
                        className="mt-1.5"
                      />
                    </div>
                    {customerAuthError ? (
                      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {customerAuthError}
                      </div>
                    ) : null}
                    {issuedCredentials ? (
                      <div className="rounded-3xl border border-[var(--rsi-purple)]/15 bg-[var(--rsi-purple-tint)] p-5">
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--rsi-purple)]/70">New customer access issued</div>
                        <div className="mt-2 text-sm text-gray-700">
                          Save these credentials for this customer. They can use them to open only their own workspace inside the app.
                        </div>
                        <div className="mt-3 font-mono-plex text-sm text-[var(--rsi-navy)]">
                          ID: {issuedCredentials.customer_access_id}
                        </div>
                        <div className="mt-1 font-mono-plex text-sm text-[var(--rsi-navy)]">
                          Code: {issuedCredentials.access_code}
                        </div>
                      </div>
                    ) : null}
                    <div className="workspace-form-actions">
                      <button type="submit" className="btn-primary workspace-form-primary" disabled={customerSession.loading}>
                        {customerSession.loading ? "Opening..." : "Open my workspace"}
                      </button>
                      <button type="button" onClick={() => setActiveTab("track")} className="btn-ghost workspace-form-secondary">
                        Use public tracking
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>

            <div className="app-tab-rail mt-8 hidden md:flex flex-wrap gap-2 rounded-2xl bg-[var(--rsi-bg-subtle)] p-2">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`app-tab-button inline-flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                    activeTab === tab.key
                      ? "app-tab-button--active bg-white text-[var(--rsi-purple)] shadow-sm"
                      : "text-gray-600"
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                  {tab.key === "notifications" && unreadCount ? (
                    <span className="inline-flex min-w-5 justify-center rounded-full bg-[var(--rsi-purple)] px-1.5 py-0.5 text-[10px] text-white">
                      {unreadCount}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>

            {activeTab === "shop" ? (
              <div className="mt-6">
                <div className="grid xl:grid-cols-[1.1fr_0.9fr] gap-6">
                  <div className="glass-dark rounded-[28px] p-6 text-white">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="badge-purple !border-white/15 !bg-white/10 !text-white">
                        <Sparkles className="h-3.5 w-3.5" /> Smart app assistant
                      </span>
                      {smartCategorySlugs[0] ? (
                        <span className="badge-purple !border-white/15 !bg-white/10 !text-white">
                          Best match: {categoryNameMap[smartCategorySlugs[0]] || smartCategorySlugs[0]}
                        </span>
                      ) : null}
                    </div>
                    <h2 className="mt-4 font-display text-3xl font-black tracking-tight">
                      The app now adapts to customer intent, not just taps.
                    </h2>
                    <p className="mt-3 max-w-2xl text-sm sm:text-base text-white/75 leading-relaxed">
                      Product ranking, quick actions, alerts, and tracking shortcuts now react to
                      saved preferences, current cart behavior, and recent order activity.
                    </p>
                    <div className="mt-5 grid sm:grid-cols-3 gap-3 text-sm">
                      <div className="rounded-3xl bg-white/10 p-4">
                        <div className="text-white/60 uppercase tracking-[0.18em] text-[11px]">
                          Smart rank
                        </div>
                        <div className="mt-2 font-display text-2xl font-black">
                          {displayedProducts.length}
                        </div>
                        <div className="mt-1 text-white/70">
                          Products sorted by likely relevance.
                        </div>
                      </div>
                      <div className="rounded-3xl bg-white/10 p-4">
                        <div className="text-white/60 uppercase tracking-[0.18em] text-[11px]">
                          Active watches
                        </div>
                        <div className="mt-2 font-display text-2xl font-black">
                          {savedTrackingIds.length + savedOrderNumbers.length}
                        </div>
                        <div className="mt-1 text-white/70">
                          Saved tracking and order shortcuts.
                        </div>
                      </div>
                      <div className="rounded-3xl bg-white/10 p-4">
                        <div className="text-white/60 uppercase tracking-[0.18em] text-[11px]">
                          Alert mix
                        </div>
                        <div className="mt-2 font-display text-2xl font-black">
                          {alertSummary.total}
                        </div>
                        <div className="mt-1 text-white/70">
                          Product, order, shipment, and delivery alerts.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rsi-card p-6">
                    <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                      Suggested next steps
                    </div>
                    <div className="mt-4 space-y-3">
                      {smartActions.length ? (
                        smartActions.map((action) => (
                          <button
                            key={action.label}
                            type="button"
                            onClick={action.onClick}
                            className="w-full rounded-3xl border border-gray-100 bg-[var(--rsi-bg-subtle)] px-4 py-4 text-left transition hover:border-[var(--rsi-purple)]/20 hover:bg-[var(--rsi-purple-tint)]"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="font-semibold text-[var(--rsi-navy)]">
                                  {action.label}
                                </div>
                                <div className="mt-1 text-sm text-gray-600">{action.note}</div>
                              </div>
                              <ArrowUpRight className="h-4 w-4 text-[var(--rsi-purple)]" />
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="rounded-3xl border border-dashed border-gray-200 p-6 text-sm text-gray-500">
                          Start browsing, save alert preferences, or track a shipment to let the
                          app learn your workflow.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="rsi-card p-5">
                  <div className="grid lg:grid-cols-[minmax(0,1fr)_auto] gap-4 items-center">
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search products, origin, HS code, or price..."
                        className="pl-10 h-12 rounded-full"
                        data-testid="app-products-search"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setCategory("all")}
                        className={`px-4 py-2 rounded-full text-sm font-medium ${
                          category === "all"
                            ? "bg-[var(--rsi-purple)] text-white"
                            : "bg-white border border-gray-200 text-gray-700"
                        }`}
                      >
                        All
                      </button>
                      {categories.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setCategory(item.slug)}
                          className={`px-4 py-2 rounded-full text-sm font-medium ${
                            category === item.slug
                              ? "bg-[var(--rsi-purple)] text-white"
                              : "bg-white border border-gray-200 text-gray-700"
                          }`}
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  {smartSearchChips.length ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {smartSearchChips.map((chip) => (
                        <button
                          key={chip.label}
                          type="button"
                          onClick={chip.onClick}
                          className="inline-flex items-center gap-2 rounded-full bg-[var(--rsi-purple-tint)] px-3 py-1.5 text-xs font-semibold text-[var(--rsi-purple)]"
                        >
                          <Clock3 className="h-3.5 w-3.5" />
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                {displayedProducts.length > 0 ? (
                  <div className="mt-6 rsi-card p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          Smart picks
                        </div>
                        <h3 className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                          Best matches for this customer session
                        </h3>
                      </div>
                      {smartCategorySlugs[0] ? (
                        <button
                          type="button"
                          onClick={() => setCategory(smartCategorySlugs[0])}
                          className="btn-ghost !px-4 !py-2.5 text-xs"
                        >
                          Focus {categoryNameMap[smartCategorySlugs[0]] || smartCategorySlugs[0]}
                        </button>
                      ) : null}
                    </div>
                    <div className="mt-5 grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                      {displayedProducts.slice(0, 3).map((product) => (
                        <div key={product.id} className="rounded-3xl border border-gray-100 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-semibold text-[var(--rsi-navy)]">
                                {product.name}
                              </div>
                              <div className="mt-1 text-sm text-gray-600 line-clamp-2">
                                {product.short_description}
                              </div>
                            </div>
                            <span className="badge-purple">Smart pick</span>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2 text-xs">
                            <span className="badge-navy">
                              {categoryNameMap[product.category_slug] || product.category_slug}
                            </span>
                            {product.origin_country ? (
                              <span className="badge-purple">{product.origin_country}</span>
                            ) : null}
                          </div>
                          <div className="mt-4 flex gap-3">
                            <Link
                              to={`/products/${product.slug}`}
                              className="btn-ghost flex-1 justify-center !py-2.5 text-sm"
                            >
                              View
                            </Link>
                            <button
                              type="button"
                              onClick={() => {
                                cart.addItem(product, 1);
                                toast.success("Added to cart");
                              }}
                              className="btn-primary flex-1 !py-2.5 text-sm"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {featuredProducts.length > 0 ? (
                  <div className="mt-6 grid lg:grid-cols-3 gap-5">
                    {featuredProducts.map((product) => (
                      <div key={product.id} className="glass-dark rounded-3xl p-5 text-white">
                        <div className="text-xs uppercase tracking-[0.18em] text-white/50">
                          New arrival spotlight
                        </div>
                        <div className="mt-3 font-display text-2xl font-black tracking-tight">
                          {product.name}
                        </div>
                        <div className="mt-2 text-sm text-white/70 line-clamp-2">
                          {product.short_description}
                        </div>
                        <div className="mt-4 flex items-center justify-between text-xs text-white/70">
                          <span>{product.price_range || "Price on request"}</span>
                          <span>{product.moq || "MOQ on request"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {isLoading ? (
                    <div className="col-span-full text-center py-16 text-gray-500">
                      Loading app catalog…
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="col-span-full rsi-card p-10 text-center text-gray-500">
                      {products.length === 0
                        ? "No live products are published yet. Add real catalog entries from the admin panel to populate this app."
                        : "No products matched your search or category filter."}
                    </div>
                  ) : (
                    displayedProducts.map((product) => {
                      const qty = qtyMap[product.id] || 1;
                      return (
                        <div key={product.id} className="rsi-card overflow-hidden flex flex-col">
                          <Link to={`/products/${product.slug}`} className="block">
                            <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                              <img
                                src={product.image_url}
                                alt={product.name}
                                className="h-full w-full object-cover"
                              />
                            </div>
                          </Link>
                          <div className="p-5 flex-1 flex flex-col">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="badge-navy">{product.category_slug}</span>
                              {product.origin_country ? (
                                <span className="text-xs text-gray-500">
                                  {product.origin_country}
                                </span>
                              ) : null}
                            </div>
                            <Link to={`/products/${product.slug}`} className="mt-3">
                              <h3 className="font-display text-xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
                                {product.name}
                              </h3>
                            </Link>
                            <p className="mt-2 text-sm text-gray-600 line-clamp-2 flex-1">
                              {product.short_description}
                            </p>

                            <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                              <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] p-3">
                                <div className="text-gray-400 uppercase tracking-wider">
                                  Price
                                </div>
                                <div className="mt-1 font-mono-plex text-[var(--rsi-navy)]">
                                  {product.price_range || "On request"}
                                </div>
                              </div>
                              <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] p-3">
                                <div className="text-gray-400 uppercase tracking-wider">
                                  MOQ
                                </div>
                                <div className="mt-1 font-mono-plex text-[var(--rsi-navy)]">
                                  {product.moq || "—"}
                                </div>
                              </div>
                            </div>

                            <div className="mt-5 flex items-center justify-between gap-3">
                              <div className="inline-flex items-center rounded-full border border-gray-200">
                                <button
                                  type="button"
                                  onClick={() => setProductQty(product.id, qty - 1)}
                                  className="h-10 w-10 grid place-items-center text-gray-600"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                                <div className="min-w-[36px] text-center text-sm font-semibold text-[var(--rsi-navy)]">
                                  {qty}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setProductQty(product.id, qty + 1)}
                                  className="h-10 w-10 grid place-items-center text-gray-600"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  cart.addItem(product, qty);
                                  toast.success("Added to cart");
                                }}
                                className="btn-primary !py-2.5 text-sm"
                                data-testid={`app-add-to-cart-${product.slug}`}
                              >
                                <ShoppingCart className="h-4 w-4" /> Add to cart
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : null}

            {activeTab === "notifications" ? (
              <div className="mt-6 grid xl:grid-cols-[0.95fr_1.05fr] gap-6">
                <div className="rsi-card p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="badge-purple">
                        <Bell className="h-3.5 w-3.5" /> Notification center
                      </span>
                      <h2 className="font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)] mt-4">
                        Alerts for new products and shipments.
                      </h2>
                    </div>
                    <button
                      type="button"
                      onClick={() => notifications.requestPermission()}
                      className="btn-ghost !px-4 !py-2.5 text-xs"
                    >
                      {notifications.permission === "granted" ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : (
                        <Bell className="h-4 w-4" />
                      )}
                      {notifications.permission === "granted" ? "Enabled" : "Enable alerts"}
                    </button>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2 text-xs">
                    <span className="badge-purple">Android friendly</span>
                    <span className="badge-purple">iPhone safe-area ready</span>
                    <span className="badge-purple">Periodic inbox refresh</span>
                    <span className="badge-purple">Shipment update alerts</span>
                  </div>

                  <div className="mt-5 grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
                    <div className="rounded-3xl bg-[var(--rsi-bg-subtle)] p-4">
                      <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400">
                        Products
                      </div>
                      <div className="mt-2 font-display text-2xl font-black text-[var(--rsi-navy)]">
                        {alertSummary.product}
                      </div>
                    </div>
                    <div className="rounded-3xl bg-[var(--rsi-bg-subtle)] p-4">
                      <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400">
                        Shipments
                      </div>
                      <div className="mt-2 font-display text-2xl font-black text-[var(--rsi-navy)]">
                        {alertSummary.shipment}
                      </div>
                    </div>
                    <div className="rounded-3xl bg-[var(--rsi-bg-subtle)] p-4">
                      <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400">
                        Deliveries
                      </div>
                      <div className="mt-2 font-display text-2xl font-black text-[var(--rsi-navy)]">
                        {alertSummary.delivery}
                      </div>
                    </div>
                    <div className="rounded-3xl bg-[var(--rsi-bg-subtle)] p-4">
                      <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400">
                        Orders
                      </div>
                      <div className="mt-2 font-display text-2xl font-black text-[var(--rsi-navy)]">
                        {alertSummary.order}
                      </div>
                    </div>
                  </div>

                  <form
                    className="mt-6 grid gap-4"
                    onSubmit={async (event) => {
                      event.preventDefault();
                      await notifications.saveSubscription(notifications.profile);
                    }}
                  >
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="notif-name">Customer Name</Label>
                        <Input
                          id="notif-name"
                          value={notifications.profile.customer_name || ""}
                          onChange={(event) =>
                            notifications.setProfile((current) => ({
                              ...current,
                              customer_name: event.target.value,
                            }))
                          }
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="notif-email">Email</Label>
                        <Input
                          id="notif-email"
                          type="email"
                          value={notifications.profile.email || ""}
                          onChange={(event) =>
                            notifications.setProfile((current) => ({
                              ...current,
                              email: event.target.value,
                            }))
                          }
                          className="mt-1.5"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="notif-phone">Phone / WhatsApp</Label>
                        <Input
                          id="notif-phone"
                          value={notifications.profile.phone || ""}
                          onChange={(event) =>
                            notifications.setProfile((current) => ({
                              ...current,
                              phone: event.target.value,
                            }))
                          }
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="notif-categories">Interested Categories</Label>
                        <Input
                          id="notif-categories"
                          placeholder="textiles, electronics, agro"
                          value={(notifications.profile.interested_categories || []).join(", ")}
                          onChange={(event) =>
                            notifications.setProfile((current) => ({
                              ...current,
                              interested_categories: parseListInput(event.target.value),
                            }))
                          }
                          className="mt-1.5"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="notif-tracking">Tracking IDs</Label>
                        <Input
                          id="notif-tracking"
                          placeholder="RSI-2026-0001, RSI-2026-0002"
                          value={(notifications.profile.tracking_ids || []).join(", ")}
                          onChange={(event) =>
                            notifications.setProfile((current) => ({
                              ...current,
                              tracking_ids: parseListInput(event.target.value).map((item) =>
                                item.toUpperCase()
                              ),
                            }))
                          }
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="notif-orders">Order Numbers</Label>
                        <Input
                          id="notif-orders"
                          placeholder="RSI-ORD-202606..."
                          value={(notifications.profile.order_numbers || []).join(", ")}
                          onChange={(event) =>
                            notifications.setProfile((current) => ({
                              ...current,
                              order_numbers: parseListInput(event.target.value).map((item) =>
                                item.toUpperCase()
                              ),
                            }))
                          }
                          className="mt-1.5"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                      {[
                        ["allow_new_products", "New products"],
                        ["allow_shipment_updates", "Shipment progress"],
                        ["allow_delivery_updates", "Delivery arrival"],
                        ["allow_order_updates", "Order updates"],
                        ["allow_promotions", "Promotions"],
                        ["allow_email", "Email messages"],
                      ].map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() =>
                            notifications.setProfile((current) => ({
                              ...current,
                              [key]: !current[key],
                            }))
                          }
                          className={`rounded-2xl border px-4 py-3 text-left text-sm ${
                            notifications.profile[key]
                              ? "border-[var(--rsi-purple)] bg-[var(--rsi-purple-tint)] text-[var(--rsi-purple)]"
                              : "border-gray-200 bg-white text-gray-600"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={notifications.saveSubscriptionMut.isPending}
                      >
                        Save alert preferences
                      </button>
                      <button
                        type="button"
                        onClick={() => notifications.requestPermission()}
                        className="btn-ghost"
                      >
                        Browser notification permission
                      </button>
                    </div>
                  </form>
                </div>

                <div className="rsi-card p-6">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                        Latest alerts
                      </div>
                      <h3 className="font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)] mt-2">
                        Customer inbox
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => notifications.refreshNotifications()}
                      className="btn-ghost !px-4 !py-2.5 text-xs"
                    >
                      <RefreshCcw className="h-4 w-4" />
                      {notifications.notificationsRefreshing ? "Refreshing..." : "Refresh inbox"}
                    </button>
                  </div>

                  <div className="mt-5 space-y-3">
                    {notifications.notificationsLoading ? (
                      <div className="text-sm text-gray-500 py-12 text-center">
                        Loading notifications…
                      </div>
                    ) : notifications.notifications.length === 0 ? (
                      <div className="rounded-3xl border border-dashed border-gray-200 p-8 text-center text-gray-500">
                        No alerts yet. Save your preferences to receive product and shipment
                        updates.
                      </div>
                    ) : (
                      notifications.notifications.map((item) => (
                        <div key={item.id} className="rounded-3xl border border-gray-100 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                                {item.type}
                              </div>
                              <div className="font-semibold text-[var(--rsi-navy)] mt-1">
                                {item.title}
                              </div>
                            </div>
                            <div className="text-[11px] text-gray-400">
                              {new Date(item.created_at).toLocaleString()}
                            </div>
                          </div>
                          <div className="mt-2 text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                            {item.body}
                          </div>
                          {item.cta_url ? (
                            <div className="mt-3">
                              <Link
                                to={item.cta_url}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--rsi-purple)]"
                              >
                                {item.cta_label || "Open"} <ArrowUpRight className="h-4 w-4" />
                              </Link>
                            </div>
                          ) : null}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : null}

            {activeTab === "track" ? (
              <div className="mt-6 grid lg:grid-cols-[1.15fr_0.85fr] gap-6">
                <div className="rsi-card p-6">
                  <span className="badge-purple">
                    <Truck className="h-3.5 w-3.5" /> Shipment access
                  </span>
                  <h2 className="font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)] mt-4">
                    Jump directly into shipment tracking.
                  </h2>
                  <p className="mt-3 text-gray-600 max-w-xl">
                    Open the live tracking screen with checkpoint progress, ETA visibility,
                    delivery status, and the alert system you configured in this app.
                  </p>
                  <form onSubmit={openTrack} className="mt-6 flex flex-col sm:flex-row gap-3">
                    <Input
                      value={trackId}
                      onChange={(event) => setTrackId(event.target.value)}
                      placeholder="Enter tracking ID e.g. RSI-2026-0001"
                      className="h-12 rounded-full"
                    />
                    <button type="submit" className="btn-primary">
                      Open tracking <ArrowUpRight className="h-4 w-4" />
                    </button>
                  </form>
                  {savedTrackingIds.length ? (
                    <div className="mt-4">
                      <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                        Saved tracking shortcuts
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {savedTrackingIds.slice(0, 6).map((item) => (
                          <button
                            key={item}
                            type="button"
                            onClick={() => setTrackId(item)}
                            className="inline-flex items-center gap-2 rounded-full bg-[var(--rsi-purple-tint)] px-3 py-1.5 text-xs font-semibold text-[var(--rsi-purple)]"
                          >
                            <Truck className="h-3.5 w-3.5" /> {item}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="rsi-card p-6">
                  <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                    Mobile app reliability
                  </div>
                  <div className="mt-4 space-y-4">
                    <div className="flex gap-3">
                      <div className="h-10 w-10 rounded-2xl bg-[var(--rsi-purple-tint)] text-[var(--rsi-purple)] grid place-items-center">
                        <Package2 className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-[var(--rsi-navy)]">
                          Admin-synced product launches
                        </div>
                        <div className="text-sm text-gray-600">
                          New products can now surface in the app notification flow.
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="h-10 w-10 rounded-2xl bg-[var(--rsi-purple-tint)] text-[var(--rsi-purple)] grid place-items-center">
                        <Bell className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-[var(--rsi-navy)]">
                          Periodic app inbox refresh
                        </div>
                        <div className="text-sm text-gray-600">
                          The app regularly refreshes customer alerts while open.
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="h-10 w-10 rounded-2xl bg-[var(--rsi-purple-tint)] text-[var(--rsi-purple)] grid place-items-center">
                        <ShieldCheck className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-[var(--rsi-navy)]">
                          iPhone and Android safer layout
                        </div>
                        <div className="text-sm text-gray-600">
                          The app shell is tuned for touch targets, install mode, and safe areas.
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="h-10 w-10 rounded-2xl bg-[var(--rsi-purple-tint)] text-[var(--rsi-purple)] grid place-items-center">
                        <Sparkles className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-[var(--rsi-navy)]">
                          Smart watchlist memory
                        </div>
                        <div className="text-sm text-gray-600">
                          Tracking IDs you search are remembered and reused for faster follow-up.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <div className="app-bottom-nav md:hidden">
        <div className="container-rsi">
          <div className="app-bottom-nav__inner">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`app-bottom-nav__button ${activeTab === tab.key ? "is-active" : ""}`}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.label}</span>
                {tab.key === "notifications" && unreadCount ? <i>{unreadCount}</i> : null}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto bg-white">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
              App Cart
            </SheetTitle>
            <SheetDescription>
              Review products selected inside the app and continue to checkout.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {cart.items.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-gray-200 p-8 text-center text-gray-500">
                Your app cart is empty.
              </div>
            ) : (
              cart.items.map((item) => (
                <div key={item.product_id} className="rounded-3xl border border-gray-100 p-4">
                  <div className="flex gap-4">
                    <img
                      src={item.image_url}
                      alt={item.product_name}
                      className="h-20 w-20 rounded-2xl object-cover bg-gray-100"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-[var(--rsi-navy)]">
                            {item.product_name}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {item.price_label || "Price on request"}
                          </div>
                          {item.moq_label ? (
                            <div className="text-xs text-gray-500">
                              MOQ: {item.moq_label}
                            </div>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={() => cart.removeItem(item.product_id)}
                          className="h-9 w-9 rounded-full grid place-items-center hover:bg-gray-100"
                        >
                          <X className="h-4 w-4 text-gray-500" />
                        </button>
                      </div>

                      <div className="mt-4 inline-flex items-center rounded-full border border-gray-200">
                        <button
                          type="button"
                          onClick={() => cart.updateQuantity(item.product_id, item.quantity - 1)}
                          className="h-10 w-10 grid place-items-center text-gray-600"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <div className="min-w-[36px] text-center text-sm font-semibold text-[var(--rsi-navy)]">
                          {item.quantity}
                        </div>
                        <button
                          type="button"
                          onClick={() => cart.updateQuantity(item.product_id, item.quantity + 1)}
                          className="h-10 w-10 grid place-items-center text-gray-600"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mt-6 rounded-3xl bg-[var(--rsi-bg-subtle)] p-5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Line items</span>
              <span className="font-semibold text-[var(--rsi-navy)]">{cart.lineItems}</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="text-gray-500">Total units</span>
              <span className="font-semibold text-[var(--rsi-navy)]">{cart.totalUnits}</span>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={() => cart.clearCart()}
              className="btn-ghost flex-1"
              disabled={!cart.items.length}
            >
              Clear cart
            </button>
            <button
              type="button"
              onClick={() => setCheckoutOpen(true)}
              className="btn-primary flex-1"
              disabled={!cart.items.length}
            >
              Proceed to checkout
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
              App Checkout
            </DialogTitle>
            <DialogDescription>
              Submit the order from the app. The trade desk can then manage the order and
              future alerts from the admin panel.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submitOrder} className="grid gap-4 mt-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="customer_name">Customer Name *</Label>
                <Input
                  id="customer_name"
                  required
                  value={checkout.customer_name}
                  onChange={(event) =>
                    setCheckout((current) => ({
                      ...current,
                      customer_name: event.target.value,
                    }))
                  }
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  value={checkout.company_name}
                  onChange={(event) =>
                    setCheckout((current) => ({
                      ...current,
                      company_name: event.target.value,
                    }))
                  }
                  className="mt-1.5"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Business Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={checkout.email}
                  onChange={(event) =>
                    setCheckout((current) => ({
                      ...current,
                      email: event.target.value,
                    }))
                  }
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone / WhatsApp *</Label>
                <Input
                  id="phone"
                  required
                  value={checkout.phone}
                  onChange={(event) =>
                    setCheckout((current) => ({
                      ...current,
                      phone: event.target.value,
                    }))
                  }
                  className="mt-1.5"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="country">Country *</Label>
                <Input
                  id="country"
                  required
                  value={checkout.country}
                  onChange={(event) =>
                    setCheckout((current) => ({
                      ...current,
                      country: event.target.value,
                    }))
                  }
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={checkout.city}
                  onChange={(event) =>
                    setCheckout((current) => ({
                      ...current,
                      city: event.target.value,
                    }))
                  }
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Preferred Contact</Label>
                <Select
                  value={checkout.preferred_contact}
                  onValueChange={(value) =>
                    setCheckout((current) => ({
                      ...current,
                      preferred_contact: value,
                    }))
                  }
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="address">Delivery Address *</Label>
              <Textarea
                id="address"
                required
                rows={3}
                value={checkout.address}
                onChange={(event) =>
                  setCheckout((current) => ({
                    ...current,
                    address: event.target.value,
                  }))
                }
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="notes">Order Notes</Label>
              <Textarea
                id="notes"
                rows={4}
                placeholder="Packaging instructions, target timeline, import notes, preferred port, or other order details..."
                value={checkout.notes}
                onChange={(event) =>
                  setCheckout((current) => ({
                    ...current,
                    notes: event.target.value,
                  }))
                }
                className="mt-1.5"
              />
            </div>

            <div className="rounded-3xl bg-[var(--rsi-bg-subtle)] p-5">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                Order summary
              </div>
              <div className="mt-3 space-y-2 text-sm">
                {cart.items.map((item) => (
                  <div
                    key={item.product_id}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="text-gray-600 truncate">{item.product_name}</span>
                    <span className="font-semibold text-[var(--rsi-navy)]">
                      x {item.quantity}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={placeOrderMut.isPending}
            >
              {placeOrderMut.isPending ? "Submitting order..." : "Place order from app"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!orderPlaced} onOpenChange={(open) => !open && setOrderPlaced(null)}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
              Order received
            </DialogTitle>
            <DialogDescription>
              Your order request is now inside the admin panel and linked into the notification
              flow.
            </DialogDescription>
          </DialogHeader>
          {orderPlaced ? (
            <div className="mt-4 space-y-4">
              <div className="rounded-3xl bg-[var(--rsi-bg-subtle)] p-5">
                <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                  Order number
                </div>
                <div className="mt-2 font-mono-plex text-lg font-semibold text-[var(--rsi-navy)]">
                  {activeOrder?.order_number}
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  Save notification preferences to receive app alerts for order progress and
                  future shipment movement.
                </div>
              </div>
              {issuedCredentials ? (
                <div className="rounded-3xl border border-[var(--rsi-purple)]/15 bg-[var(--rsi-purple-tint)] p-5">
                  <div className="text-xs uppercase tracking-[0.18em] text-[var(--rsi-purple)]/70">
                    Customer access credentials
                  </div>
                  <div className="mt-2 text-sm text-gray-700">
                    Save this access ID and access code. This customer can now open only their own
                    orders and shipments inside the app workspace.
                  </div>
                  <div className="mt-3 font-mono-plex text-sm text-[var(--rsi-navy)]">
                    ID: {issuedCredentials.customer_access_id}
                  </div>
                  <div className="mt-1 font-mono-plex text-sm text-[var(--rsi-navy)]">
                    Code: {issuedCredentials.access_code}
                  </div>
                </div>
              ) : null}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setOrderPlaced(null);
                    setIssuedCredentials(null);
                    setActiveTab("notifications");
                  }}
                  className="btn-primary flex-1"
                >
                  Open app workspace
                </button>
                <Link to="/track" className="btn-ghost flex-1 justify-center">
                  Open tracking
                </Link>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
